import {Badge, Button, Card, message, Table, theme} from "antd";
import {statusObj, titleHead} from "../utils/constants";
import {useLocation} from "react-router-dom";
import {useEffect} from "react";
import api from "../http";
import {Link2} from "lucide-react";
import * as analytics from "../utils/Analytics";
import {useStore} from "../contexts/AppContext";
import MainTableModal from "./MainTableModal";
import AccessRequestForm from "./AccessRequestForm";
import {useFetchData} from "../hooks/useFetchData";


const AccessRequestsTable = () => {
    const fetchData = useFetchData();
    const url = useLocation();
    const clearUrl = url.pathname.replace('/dashboard/', '')
    const {token} = theme.useToken();
    const {setMode, setRow, open, setOpen, setTab, setRequestAccessData,
        accessResponses, setAccessResponses, setDataLoading, dataLoading} = useStore()


    const columns = [
        {
            title: '№',
            render: (_, rowData, rowIndex) => {
                return <span>{rowIndex + 1}</span>
            },
            width: 50
        },
        {
            key: 'document_id',
            dataIndex: "document_id",
            title: 'Id',
            width: 300

        },
        {
            key: 'document_title',
            dataIndex: "document_title",
            title: 'Title'

        },
        {
            key: 'document_url',
            dataIndex: "document_url",
            title: 'Url',
            render: (_, {document_url}) => {
                if (!document_url) {
                    return null;
                }
                return (
                    <div style={{display: "flex", justifyContent: "center", alignItems: "center", height: "100%"}}>
                        <Button
                            size="xs"
                            icon={<Link2 size={16}/>}
                            href={document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                );
            },
            width: 50
        },
        {
            key: 'profile_email',
            dataIndex: "profile_email",
            title: 'Requester email',
        },
        {
            key: 'approver_profile_email',
            dataIndex: "approver_profile_email",
            title: 'Approver email',
        },
        {
            key: 'role_title',
            dataIndex: "role_title",
            title: 'Role',
        },
        {
            key: 'comment',
            dataIndex: "comment",
            title: 'Comment',
        },
        {
            key: 'status',
            dataIndex: "status",
            title: 'Status',
            render: (_, {status}) => (
                <>
                    <><Badge status={statusObj[status]}/> {status}</>
                </>
            ),

        },
        {
            key: 'decision',
            dataIndex: "decision",
            title: 'Action',
            render: (_, rowData) => {
                return (
                    clearUrl === 'access-requests-to-me' ?
                        <Button
                            disabled={rowData.status !== 'pending'}
                            type="primary"
                            onClick={(e) => showModal(e, rowData, "accept")}>
                            decision
                        </Button>
                         :
                        <Button
                            disabled={rowData.status === 'approved'}
                            type="primary"
                            onClick={(e) => showModal(e, rowData, "request")}>
                            request
                        </Button>
                );
            },
            width: 100
        },
        {
            key: 'created_at',
            dataIndex: "created_at",
            title: 'Created at',
            render: (_, rowData) => {
                return <span>{rowData.created_at ? new Date(rowData.created_at).toLocaleString('ru-RU') : null}</span>
            },
        },
        {
            key: 'updated_at',
            dataIndex: "updated_at",
            title: 'Updated at',
            render: (_, rowData) => {
                return <span>{rowData.updated_at ? new Date(rowData.updated_at).toLocaleString('ru-RU') : null}</span>
            },
        },
    ];

    useEffect(() => {
        const fetchData = async () => {
            setDataLoading(true)
            try {
                const docResponse = await api.get(`support/${clearUrl}`);
                if (docResponse.status === 200) {
                    setAccessResponses(docResponse.data)
                }
            } catch (error) {
                console.log(error)
            } finally {
                setDataLoading(false)
            }
        }
        fetchData()
        // eslint-disable-next-line
    }, [clearUrl]);

    useEffect(() => {
        const abortController = new AbortController();
        setTab('info')
        fetchData(abortController.signal);

        return () => {
            abortController.abort();
        };
        // eslint-disable-next-line
    }, []);

    const onRowClick = async (rowData) => {
        try {
            const documentResponse = await api.get(`supabase/document/${rowData.document_id}`);
            if (documentResponse.status === 200) {
                setRow(documentResponse.data)
                setMode("editDocument")
                setOpen(true);

                const key = `doc_opened_${rowData.document_id}`;
                if (!sessionStorage.getItem(key)) {
                    analytics.sendEvent('doc_opened', {
                        doc_id: rowData?.document_id || null,
                        doc_url: rowData?.document_url || null
                    });
                    sessionStorage.setItem(key, '1');
                }
            }
        } catch (e) {
            message.error(e.response.data.message);
        }

    }


    const handleClose = () => {
        setOpen(false);
        setMode("")
        setTab("info")
    }

    const showModal = (e, rowData, mode) => {
        e.stopPropagation()
        if (mode === "accept") {
            setRequestAccessData((prevState => ({...prevState,
                document_id: rowData.document_id,
                requester_id: rowData.profile_id,
                requester_email: rowData.profile_email,
                access_form_modal: true,
                access_accept_access_mode: 'accept',
                access_form_mode: 'editOld',
                access_form: rowData
            })))
        } else if (mode === "request") {
            setRequestAccessData((prevState => ({...prevState,
                document_id: rowData.document_id,
                access_form_modal: true,
                access_accept_access_mode: 'request',
                access_form_mode: 'editOld',
                access_form: rowData
            })))
        }

    };



    return (
        <div
            style={{
                width: "100vw",
                minHeight: "90vh",
                background: token.colorBgContainer,
                margin: 30,
                overflowX: "hidden",
                borderRadius: 10,
            }}
        >
            <Card
                title={titleHead[clearUrl]}
                variant={'borderless'}
                style={{
                    width: "100%",
                    height: "100%",
                    overflowX: "auto",
                    background: token.colorBgContainer,
                }}
            >
                <>
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={accessResponses}
                        loading={dataLoading}
                        size={"small"}
                        scroll={{x: "max-content", y: "calc(100vh - 320px)"}}
                        onRow={(rowData) => ({
                            onClick: () => onRowClick(rowData),
                        })}
                    />
                </>
                <MainTableModal open={open} handleClose={handleClose}/>
                <AccessRequestForm/>
            </Card>
        </div>
    )
}

export default AccessRequestsTable